import { useState, useEffect, useCallback, Fragment } from 'react';
import {
  BookMarked,
  Plus,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Upload,
  Download,
  CheckCircle,
  RotateCcw,
  Trash2,
  AlertCircle,
  FileText,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import { journalsApi, periodsApi, entitiesApi, accountsApi } from '@/api/client';

// --- Types -------------------------------------------------------------------

interface JournalLine {
  id: number;
  journal_entry_id: number;
  entity_id?: number | null;
  entity_name?: string;
  entity_code?: string;
  account_id?: number | null;
  account_code?: string;
  account_name?: string;
  debit: string;
  credit: string;
  currency: string;
  description?: string;
}

interface JournalEntryData {
  id: number;
  tenant_id: number;
  period_id: number;
  period_label?: string;
  entry_type: string;
  description: string;
  source_skill?: string;
  status: string;
  is_manual: boolean;
  reference?: string;
  created_by?: number;
  created_by_name?: string;
  posted_at?: string;
  reversed_at?: string;
  total_debit: string;
  total_credit: string;
  line_count: number;
  lines: JournalLine[];
  created_at: string;
  updated_at: string;
}

interface Period {
  id: string;
  year: number;
  month: number;
  status: string;
}

interface Entity {
  id: string;
  code: string;
  name: string;
}

interface GroupAccount {
  id: string;
  code: string;
  name: string;
  account_type: string;
}

// --- Helpers -----------------------------------------------------------------

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatAmount(val: string | number): string {
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (!num || num === 0) return '-';
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Line-level amounts: show 0.00 so rows are not mistaken for missing data. */
function formatLineAmount(val: string | number): string {
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (num === null || num === undefined || Number.isNaN(num)) return '-';
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function statusBadge(s: string) {
  switch (s) {
    case 'posted':
      return { bg: 'bg-green-50 border-green-200', text: 'text-green-700', label: 'Posted' };
    case 'draft':
      return { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700', label: 'Draft' };
    case 'reversed':
      return { bg: 'bg-red-50 border-red-200', text: 'text-red-700', label: 'Reversed' };
    default:
      return { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-700', label: s };
  }
}

function typeBadge(t: string) {
  const map: Record<string, { bg: string; text: string }> = {
    elimination: { bg: 'bg-purple-50', text: 'text-purple-700' },
    adjustment: { bg: 'bg-blue-50', text: 'text-blue-700' },
    translation: { bg: 'bg-cyan-50', text: 'text-cyan-700' },
    tax: { bg: 'bg-orange-50', text: 'text-orange-700' },
    manual: { bg: 'bg-indigo-50', text: 'text-indigo-700' },
    topside: { bg: 'bg-pink-50', text: 'text-pink-700' },
    reclassification: { bg: 'bg-teal-50', text: 'text-teal-700' },
    recurring: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
    auto_reversal: { bg: 'bg-red-50', text: 'text-red-700' },
  };
  const style = map[t] || { bg: 'bg-gray-50', text: 'text-gray-700' };
  return style;
}

const ENTRY_TYPES = [
  'elimination', 'adjustment', 'reclassification', 'tax', 'translation',
  'ic_elimination', 'fx_translation', 'equity_elimination', 'minority_interest',
  'manual', 'topside', 'recurring', 'auto_reversal',
];

// --- Component ---------------------------------------------------------------

export default function JournalEntries() {
  // Data
  const [journals, setJournals] = useState<JournalEntryData[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [accounts, setAccounts] = useState<GroupAccount[]>([]);

  // Filters
  const [periodFilter, setPeriodFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // UI state
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Load periods, entities, accounts on mount
  useEffect(() => {
    Promise.all([
      periodsApi.list().catch(() => []),
      entitiesApi.list({ page_size: '500' } as any).catch(() => ({ items: [] })),
      accountsApi.listGroup().catch(() => []),
    ]).then(([p, e, a]) => {
      const periodsData = Array.isArray(p) ? p : (p as any)?.items || [];
      setPeriods(periodsData);
      const entitiesData = Array.isArray(e) ? e : (e as any)?.items || [];
      setEntities(entitiesData);
      setAccounts(Array.isArray(a) ? a : []);
    });
  }, []);

  // Load journals
  const loadJournals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = { page, page_size: pageSize };
      if (periodFilter) params.period_id = periodFilter;
      if (entityFilter) params.entity_id = entityFilter;
      if (typeFilter) params.entry_type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;

      const res = await journalsApi.list(params);
      setJournals(res.items || []);
      setTotal(res.total || 0);
    } catch (err: any) {
      setError(err?.detail || 'Failed to load journal entries');
    } finally {
      setLoading(false);
    }
  }, [page, periodFilter, entityFilter, typeFilter, statusFilter, searchQuery]);

  useEffect(() => {
    loadJournals();
  }, [loadJournals]);

  // Clear success message after 4s
  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(null), 4000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  // Actions
  const handlePost = async (id: number) => {
    setActionLoading(id);
    try {
      await journalsApi.post(String(id));
      setSuccessMsg('Journal entry posted successfully');
      loadJournals();
    } catch (err: any) {
      setError(err?.detail || 'Failed to post journal entry');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReverse = async (id: number) => {
    setActionLoading(id);
    try {
      await journalsApi.reverse(String(id));
      setSuccessMsg('Journal entry reversed successfully');
      loadJournals();
    } catch (err: any) {
      setError(err?.detail || 'Failed to reverse journal entry');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this draft journal entry?')) return;
    setActionLoading(id);
    try {
      await journalsApi.delete(String(id));
      setSuccessMsg('Journal entry deleted');
      loadJournals();
    } catch (err: any) {
      setError(err?.detail || 'Failed to delete journal entry');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkDelete = async (sourceSkill: string, label: string) => {
    if (!confirm(`Are you sure you want to delete all ${label} journal entries? This cannot be undone.`)) return;
    try {
      const res = await journalsApi.bulkDelete({ source_skill: sourceSkill });
      setSuccessMsg(res.message || 'Deleted successfully');
      loadJournals();
    } catch (err: any) {
      setError(err?.detail || 'Bulk delete failed');
    }
  };

  const handleExport = async () => {
    try {
      const params: Record<string, string> = {};
      if (periodFilter) params.period_id = periodFilter;
      if (typeFilter) params.entry_type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      await journalsApi.exportExcel(params);
      setSuccessMsg('Export downloaded');
    } catch (err: any) {
      setError(err?.detail || 'Export failed');
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
            <BookMarked className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Journal Entries</h1>
            <p className="text-sm text-gray-500">
              Manage consolidation journals, manual adjustments, and FCCS imports
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleBulkDelete('FCCS_IMPORT', 'FCCS imported')}
            className="btn-secondary flex items-center gap-2 text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
            Delete FCCS
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Bulk Upload
          </button>
          <button onClick={handleExport} className="btn-secondary flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Journal
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}
      {successMsg && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search description, reference..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              className="input-field pl-9 w-full"
            />
          </div>
          <select
            value={periodFilter}
            onChange={(e) => { setPeriodFilter(e.target.value); setPage(1); }}
            className="select-field"
          >
            <option value="">All Periods</option>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.year}-{String(p.month).padStart(2, '0')}
              </option>
            ))}
          </select>
          <select
            value={entityFilter}
            onChange={(e) => { setEntityFilter(e.target.value); setPage(1); }}
            className="select-field"
          >
            <option value="">All Entities</option>
            {entities.map((e) => (
              <option key={e.id} value={e.id}>
                {e.code} - {e.name}
              </option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            className="select-field"
          >
            <option value="">All Types</option>
            {ENTRY_TYPES.map((t) => (
              <option key={t} value={t}>{t.replace('_', ' ')}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="select-field"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="posted">Posted</option>
            <option value="reversed">Reversed</option>
          </select>
          <button onClick={loadJournals} className="btn-secondary p-2" title="Refresh">
            <RefreshCw className={clsx('w-4 h-4', loading && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{total}</p>
          <p className="text-xs text-gray-500">Total Entries</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-green-600">
            {journals.filter((j) => j.status === 'posted').length}
          </p>
          <p className="text-xs text-gray-500">Posted (page)</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-yellow-600">
            {journals.filter((j) => j.status === 'draft').length}
          </p>
          <p className="text-xs text-gray-500">Draft (page)</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-red-600">
            {journals.filter((j) => j.status === 'reversed').length}
          </p>
          <p className="text-xs text-gray-500">Reversed (page)</p>
        </div>
      </div>

      {/* Journal Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left font-medium text-gray-600 w-8"></th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Reference</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Period</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Description</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Source</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Debit</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Credit</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Lines</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center text-gray-400">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                    Loading...
                  </td>
                </tr>
              ) : journals.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center text-gray-400">
                    <FileText className="w-8 h-8 mx-auto mb-2" />
                    No journal entries found
                  </td>
                </tr>
              ) : (
                journals.map((je) => {
                  const isExpanded = expandedId === je.id;
                  const sBadge = statusBadge(je.status);
                  const tBadge = typeBadge(je.entry_type);

                  return (
                    <Fragment key={je.id}>
                      <tr
                        className={clsx(
                          'hover:bg-gray-50 cursor-pointer transition-colors',
                          isExpanded && 'bg-blue-50/50',
                        )}
                        onClick={() => setExpandedId(isExpanded ? null : je.id)}
                      >
                        <td className="px-4 py-3">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">
                          {je.reference || `JE-${je.id}`}
                        </td>
                        <td className="px-4 py-3">
                          <span className={clsx('inline-block px-2 py-0.5 rounded text-xs font-medium capitalize', tBadge.bg, tBadge.text)}>
                            {je.entry_type.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{je.period_label}</td>
                        <td className="px-4 py-3 max-w-[250px] truncate" title={je.description}>
                          {je.description}
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {je.source_skill || (je.is_manual ? 'Manual' : '-')}
                        </td>
                        <td className="px-4 py-3">
                          <span className={clsx('inline-block px-2 py-0.5 rounded border text-xs font-medium', sBadge.bg, sBadge.text)}>
                            {sBadge.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {formatAmount(je.total_debit)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {formatAmount(je.total_credit)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-500">{je.line_count}</td>
                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-1">
                            {je.status === 'draft' && (
                              <>
                                <button
                                  onClick={() => handlePost(je.id)}
                                  disabled={actionLoading === je.id}
                                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                                  title="Post"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(je.id)}
                                  disabled={actionLoading === je.id}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            {je.status === 'posted' && (
                              <button
                                onClick={() => handleReverse(je.id)}
                                disabled={actionLoading === je.id}
                                className="p-1 text-orange-600 hover:bg-orange-50 rounded"
                                title="Reverse"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Expanded detail rows */}
                      {isExpanded && je.lines.length > 0 && (
                        <tr>
                          <td colSpan={11} className="bg-gray-50/80 px-4 py-0">
                            <div className="py-3">
                              <div className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-2">
                                Journal Lines ({je.lines.length})
                                {je.created_by_name && (
                                  <span className="text-gray-400">| Created by {je.created_by_name}</span>
                                )}
                                {je.posted_at && (
                                  <span className="text-gray-400">| Posted {formatDate(je.posted_at)}</span>
                                )}
                              </div>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b border-gray-200">
                                    <th className="px-3 py-1.5 text-left font-medium text-gray-500">Entity</th>
                                    <th className="px-3 py-1.5 text-left font-medium text-gray-500">Account</th>
                                    <th className="px-3 py-1.5 text-left font-medium text-gray-500">Description</th>
                                    <th className="px-3 py-1.5 text-right font-medium text-gray-500">Debit</th>
                                    <th className="px-3 py-1.5 text-right font-medium text-gray-500">Credit</th>
                                    <th className="px-3 py-1.5 text-left font-medium text-gray-500">Currency</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {je.lines.map((line) => (
                                    <tr key={line.id} className="hover:bg-white/60">
                                      <td className="px-3 py-1.5">
                                        <span className="font-mono">{line.entity_code}</span>
                                        {line.entity_name && (
                                          <span className="text-gray-400 ml-1">{line.entity_name}</span>
                                        )}
                                      </td>
                                      <td className="px-3 py-1.5">
                                        <span className="font-mono">{line.account_code}</span>
                                        {line.account_name && (
                                          <span className="text-gray-400 ml-1 truncate inline-block max-w-[200px] align-bottom" title={line.account_name}>
                                            {line.account_name}
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-3 py-1.5 text-gray-500 max-w-[200px] truncate">
                                        {line.description || '-'}
                                      </td>
                                      <td className="px-3 py-1.5 text-right font-mono">
                                        {formatLineAmount(line.debit)}
                                      </td>
                                      <td className="px-3 py-1.5 text-right font-mono">
                                        {formatLineAmount(line.credit)}
                                      </td>
                                      <td className="px-3 py-1.5 text-gray-500">{line.currency}</td>
                                    </tr>
                                  ))}
                                  {/* Totals row */}
                                  <tr className="bg-gray-100/80 font-medium">
                                    <td colSpan={3} className="px-3 py-1.5 text-right">Total</td>
                                    <td className="px-3 py-1.5 text-right font-mono">
                                      {formatAmount(je.total_debit)}
                                    </td>
                                    <td className="px-3 py-1.5 text-right font-mono">
                                      {formatAmount(je.total_credit)}
                                    </td>
                                    <td className="px-3 py-1.5">
                                      {Math.abs(parseFloat(je.total_debit) - parseFloat(je.total_credit)) > 0.01 && (
                                        <span className="text-red-500 text-[10px]">UNBALANCED</span>
                                      )}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 py-1 text-sm">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Journal Modal */}
      {showCreateModal && (
        <CreateJournalModal
          periods={periods}
          entities={entities}
          accounts={accounts}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            setSuccessMsg('Journal entry created');
            loadJournals();
          }}
        />
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          periods={periods}
          onClose={() => setShowUploadModal(false)}
          onUploaded={(msg) => {
            setShowUploadModal(false);
            setSuccessMsg(msg);
            loadJournals();
          }}
        />
      )}
    </div>
  );
}

// --- Create Journal Modal ----------------------------------------------------

interface CreateModalProps {
  periods: Period[];
  entities: Entity[];
  accounts: GroupAccount[];
  onClose: () => void;
  onCreated: () => void;
}

interface LineFormData {
  entity_id: string;
  account_id: string;
  debit: string;
  credit: string;
  currency: string;
  description: string;
}

function CreateJournalModal({ periods, entities, accounts, onClose, onCreated }: CreateModalProps) {
  const [periodId, setPeriodId] = useState('');
  const [entryType, setEntryType] = useState('manual');
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState<LineFormData[]>([
    { entity_id: '', account_id: '', debit: '', credit: '', currency: 'AED', description: '' },
    { entity_id: '', account_id: '', debit: '', credit: '', currency: 'AED', description: '' },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addLine = () => {
    setLines([...lines, { entity_id: '', account_id: '', debit: '', credit: '', currency: 'AED', description: '' }]);
  };

  const removeLine = (idx: number) => {
    if (lines.length <= 2) return;
    setLines(lines.filter((_, i) => i !== idx));
  };

  const updateLine = (idx: number, field: keyof LineFormData, value: string) => {
    const updated = [...lines];
    updated[idx] = { ...updated[idx], [field]: value };
    setLines(updated);
  };

  const totalDebit = lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
  const totalCredit = lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const handleSubmit = async () => {
    if (!periodId || !description) {
      setError('Period and description are required');
      return;
    }
    const validLines = lines.filter((l) => l.entity_id && l.account_id && (parseFloat(l.debit) > 0 || parseFloat(l.credit) > 0));
    if (validLines.length < 2) {
      setError('At least 2 valid lines are required');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await journalsApi.create({
        period_id: parseInt(periodId),
        entry_type: entryType,
        description,
        reference: reference || undefined,
        lines: validLines.map((l) => ({
          entity_id: parseInt(l.entity_id),
          account_id: parseInt(l.account_id),
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
          currency: l.currency,
          description: l.description || undefined,
        })),
      });
      onCreated();
    } catch (err: any) {
      setError(err?.detail || 'Failed to create journal entry');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold">Create Journal Entry</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Period *</label>
              <select value={periodId} onChange={(e) => setPeriodId(e.target.value)} className="select-field w-full">
                <option value="">Select period</option>
                {periods.map((p) => (
                  <option key={p.id} value={p.id}>{p.year}-{String(p.month).padStart(2, '0')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={entryType} onChange={(e) => setEntryType(e.target.value)} className="select-field w-full">
                {ENTRY_TYPES.map((t) => (
                  <option key={t} value={t}>{t.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Journal entry description"
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="External reference"
                className="input-field w-full"
              />
            </div>
          </div>

          {/* Lines */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Journal Lines</label>
              <button onClick={addLine} className="btn-secondary text-xs px-2 py-1 flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add Line
              </button>
            </div>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Entity</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Account</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 w-28">Debit</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 w-28">Credit</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                    <th className="px-3 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {lines.map((line, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-1.5">
                        <select
                          value={line.entity_id}
                          onChange={(e) => updateLine(idx, 'entity_id', e.target.value)}
                          className="select-field text-xs w-full"
                        >
                          <option value="">Entity</option>
                          {entities.map((e) => (
                            <option key={e.id} value={e.id}>{e.code} - {e.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-1.5">
                        <select
                          value={line.account_id}
                          onChange={(e) => updateLine(idx, 'account_id', e.target.value)}
                          className="select-field text-xs w-full"
                        >
                          <option value="">Account</option>
                          {accounts.map((a) => (
                            <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          type="number"
                          step="0.01"
                          value={line.debit}
                          onChange={(e) => updateLine(idx, 'debit', e.target.value)}
                          placeholder="0.00"
                          className="input-field text-xs text-right w-full"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          type="number"
                          step="0.01"
                          value={line.credit}
                          onChange={(e) => updateLine(idx, 'credit', e.target.value)}
                          placeholder="0.00"
                          className="input-field text-xs text-right w-full"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          type="text"
                          value={line.description}
                          onChange={(e) => updateLine(idx, 'description', e.target.value)}
                          placeholder="Line note"
                          className="input-field text-xs w-full"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <button
                          onClick={() => removeLine(idx)}
                          disabled={lines.length <= 2}
                          className="p-1 text-red-400 hover:text-red-600 disabled:opacity-30"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {/* Totals */}
                  <tr className="bg-gray-50 font-medium text-xs">
                    <td colSpan={2} className="px-3 py-2 text-right">Totals:</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {totalDebit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {totalCredit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-2" colSpan={2}>
                      {!isBalanced && totalDebit + totalCredit > 0 && (
                        <span className="text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> Unbalanced: diff {Math.abs(totalDebit - totalCredit).toFixed(2)}
                        </span>
                      )}
                      {isBalanced && totalDebit > 0 && (
                        <span className="text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Balanced
                        </span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="btn-primary flex items-center gap-2"
          >
            {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create Draft
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Upload Modal ------------------------------------------------------------

interface UploadModalProps {
  periods: Period[];
  onClose: () => void;
  onUploaded: (msg: string) => void;
}

function UploadModal({ periods, onClose, onUploaded }: UploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [periodId, setPeriodId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  const handleUpload = async () => {
    if (!file) {
      setError('Please select an Excel file');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const res = await journalsApi.bulkUpload(file, periodId || undefined);
      setResult(res);
      if (res.journals_created > 0) {
        onUploaded(`Uploaded ${res.journals_created} journals with ${res.lines_created} lines from ${res.rows_parsed} rows`);
      }
    } catch (err: any) {
      setError(err?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold">Bulk Upload FCCS Journals</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Period (optional)</label>
            <select value={periodId} onChange={(e) => setPeriodId(e.target.value)} className="select-field w-full">
              <option value="">Auto-detect from file</option>
              {periods.map((p) => (
                <option key={p.id} value={p.id}>{p.year}-{String(p.month).padStart(2, '0')}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">If not selected, period will be detected from the file's Period column</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Excel File</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
            <p className="text-xs text-gray-400 mt-1">
              Upload FCCS Journal Report Excel. Rows are grouped by Label into journal entries.
            </p>
          </div>

          {result && (
            <div className="p-3 bg-green-50 border border-green-200 rounded text-sm space-y-1">
              <p className="font-medium text-green-700">Upload Complete</p>
              <p className="text-green-600">Rows parsed: {result.rows_parsed}</p>
              <p className="text-green-600">Journals created: {result.journals_created}</p>
              <p className="text-green-600">Lines created: {result.lines_created}</p>
              {result.errors?.length > 0 && (
                <details className="mt-2">
                  <summary className="text-orange-600 cursor-pointer">{result.errors.length} warnings/errors</summary>
                  <div className="mt-1 max-h-32 overflow-y-auto text-xs text-orange-600">
                    {result.errors.slice(0, 20).map((e: string, i: number) => (
                      <p key={i}>{e}</p>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t">
          <button onClick={onClose} className="btn-secondary">
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              onClick={handleUpload}
              disabled={uploading || !file}
              className="btn-primary flex items-center gap-2"
            >
              {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
